/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-magic-numbers */
/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  Box3,
  BufferAttribute,
  BufferGeometry,
  Sphere,
  Vector3,
} from '@technology/web3d-plugin-threejs';
import { QuantizedMeshData } from '../types/quantized-mesh-data';
import { buildGeometryBuffers } from './construct-geometry-core';
import type { ConstructedGeometryBuffers } from './construct-geometry.types';

export function createGeometryFromBuffers(
  buffers: ConstructedGeometryBuffers
): { geometry: BufferGeometry; localOrigin: Vector3 } {
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(buffers.positions, 3));
  geometry.setAttribute('uv', new BufferAttribute(buffers.uvs, 2));
  geometry.setAttribute('normal', new BufferAttribute(buffers.normals, 3));
  geometry.setIndex(new BufferAttribute(buffers.triangleIndices, 1));
  geometry.boundingBox = new Box3(
    new Vector3(
      buffers.boundingBox.min.x,
      buffers.boundingBox.min.y,
      buffers.boundingBox.min.z
    ),
    new Vector3(
      buffers.boundingBox.max.x,
      buffers.boundingBox.max.y,
      buffers.boundingBox.max.z
    )
  );
  geometry.boundingSphere = new Sphere(
    new Vector3(
      buffers.boundingSphere.center.x,
      buffers.boundingSphere.center.y,
      buffers.boundingSphere.center.z
    ),
    buffers.boundingSphere.radius
  );

  const localOrigin = new Vector3(
    buffers.localOrigin.x,
    buffers.localOrigin.y,
    buffers.localOrigin.z
  );

  return { geometry, localOrigin };
}

export function constructGeometry(
  qmData: QuantizedMeshData,
  mapBox: Box3, // The tile's bounding box in MAP coordinates
  mapToScene: (mapCoordinates: Vector3, result: Vector3) => Vector3,
  baseHeight: number,
  initialBaseHeight: number
): { geometry: BufferGeometry; localOrigin: Vector3 } {
  const mapCoordinates = new Vector3();
  const sceneCoordinates = new Vector3();

  return createGeometryFromBuffers(
    buildGeometryBuffers({
      vertexData: qmData.vertexData,
      triangleIndices: qmData.triangleIndices,
      header: qmData.header,
      mapBox,
      baseHeight,
      initialBaseHeight,
      mapToScene: (mapX, mapY, mapZ, out) => {
        mapCoordinates.set(mapX, mapY, mapZ);
        // Seeded with the previous output so a failed transform keeps it,
        // as the worker path does.
        sceneCoordinates.set(out.x, out.y, 0);
        mapToScene(mapCoordinates, sceneCoordinates);
        out.x = sceneCoordinates.x;
        out.y = sceneCoordinates.y;
      },
    })
  );
}

// RGBA texels plus a full mipmap chain (~1/3 extra).
const TEXTURE_BYTES_PER_TEXEL_WITH_MIPMAPS = (4 * 4) / 3;

export function estimateMeshUploadBytes(
  geometry: BufferGeometry,
  texture: { image?: { width: number; height: number } } | undefined
): number {
  let bytes = geometry.getIndex()?.array.byteLength ?? 0;
  for (const attribute of Object.values(geometry.attributes)) {
    bytes += attribute.array.byteLength;
  }
  if (texture?.image) {
    bytes +=
      texture.image.width *
      texture.image.height *
      TEXTURE_BYTES_PER_TEXEL_WITH_MIPMAPS;
  }
  return Math.ceil(bytes);
}

export function approximateQuantizedMeshDataSize(
  data: QuantizedMeshData
): number {
  let size = 0;

  size += 100;

  if (data.vertexData) {
    size += data.vertexData.byteLength;
  }

  if (data.triangleIndices) {
    size += data.triangleIndices.byteLength;
  }

  if (data.westIndices) {
    size += data.westIndices.byteLength;
  }

  if (data.northIndices) {
    size += data.northIndices.byteLength;
  }

  if (data.eastIndices) {
    size += data.eastIndices.byteLength;
  }

  if (data.southIndices) {
    size += data.southIndices.byteLength;
  }

  if (data.extensions) {
    try {
      size += JSON.stringify(data.extensions).length;
    } catch (e) {
      size += 50;
    }
  }

  return size;
}
